import { APP_V2_APP_EVENTS } from "@src/appV2/lib";
import { logError, logEvent } from "@src/appV2/lib/analytics";
import { USER_EVENTS } from "@src/constants";
import { usCountyCode } from "@src/constants/phone";
import { getCognitoSingleton } from "@src/lib/cognito/src/lib/cognitoSingleton";
import { getFirebaseSingleton } from "@src/lib/firebase/src";
import { enqueueSnackbar } from "notistack";
import { Dispatch } from "redux";

import {
  getReferralRate,
  requestAgentOTP,
  updateAgentLoggedInTime,
  validateAgentOTPv2,
} from "./api";
import { onLoggedOut } from "./init";
import { ActionType, SessionAction } from "./session.model";
import { GetState } from "../store.model";

const firebaseAuth = getFirebaseSingleton();

function showErrorMessage(message: string) {
  enqueueSnackbar({
    variant: "error",
    message,
  });
}
/**
 * @description this function call the v3/requestOTP endpoint and doesn't expect any sign in token in return
 */
export const reVerifyPhoneRequestOTP =
  (phone: string) =>
  async (dispatch: Dispatch, getState: GetState): Promise<SessionAction | boolean> => {
    const { isSignup } = getState().session;

    const res = await requestAgentOTP(phone, isSignup);
    if (!isSignup) {
      if (!res || res?.error === "AgentNotFound") {
        dispatch({ type: ActionType.SET_IS_VERIFYING_DONE });
        showErrorMessage("No account found. New? Sign up today to join.");
        return false;
      }
    }
    if (res?.error) {
      logError(APP_V2_APP_EVENTS.FIREBASE_OTP_SIGN_IN_ERROR, {
        error: res.error,
        metadata: {
          hcpMobile: `${usCountyCode}${phone}`,
        },
      });
      dispatch({ type: ActionType.SET_IS_VERIFYING_DONE });
      showErrorMessage("Unable to verify phone number. Please try again.");
      return false;
    }
    return dispatch({
      type: ActionType.VERIFICATION_ID_RECEIVED,
      data: {
        verification: { phone },
      },
    });
  };

/**
 * Only used for home/signupReVerify?phoneNumber=6086086088
 */
export const signUpReverifyWithOtp =
  (code: string, phone: string) =>
  async (dispatch: Dispatch, getState: GetState): Promise<SessionAction | boolean> => {
    dispatch({
      type: ActionType.VERIFICATION_REQUESTED,
    });
    const { isSignup } = getState().session;
    const res = await validateAgentOTPv2({ phone, code, isSignup });
    if (res?.error || !res?.data?.firebaseToken) {
      logError(APP_V2_APP_EVENTS.FIREBASE_OTP_SIGN_IN_ERROR, {
        error: res.error,
        metadata: {
          hcpMobile: `${usCountyCode}${phone}`,
        },
      });
      dispatch({ type: ActionType.SET_IS_VERIFYING_DONE });
      logEvent(USER_EVENTS.TWO_FACTOR_CODE_DENIED);
      return false;
    }
    const firebaseToken = res?.data?.firebaseToken;
    try {
      await firebaseAuth.signInWithCustomToken(firebaseToken as string);
    } catch (error) {
      if (error) {
        logError(APP_V2_APP_EVENTS.FIREBASE_OTP_SIGN_IN_ERROR, {
          error,
          metadata: {
            hcpMobile: `${usCountyCode}${phone}`,
          },
        });
        dispatch({ type: ActionType.SET_IS_VERIFYING_DONE });
        showErrorMessage("Unable to Log In. Please try again.");
        logEvent(USER_EVENTS.TWO_FACTOR_CODE_DENIED);
        return false;
      }
    }

    logEvent(USER_EVENTS.TWO_FACTOR_CODE_ACCEPTED);
    // User and Agent does not exist in DB and user is currently in signup flow
    if (!isSignup) {
      updateAgentLoggedInTime();
    }
    logEvent(USER_EVENTS.SIGNED_IN);
    localStorage.removeItem("firebaseToken");
    return true;
  };

export const loginWithLink =
  (link: string, email: string) =>
  async (dispatch: Dispatch): Promise<SessionAction | boolean> => {
    dispatch({
      type: ActionType.VERIFICATION_REQUESTED,
    });

    try {
      const { error } = await firebaseAuth.signInWithEmailLink(link, email);

      if (error) {
        dispatch({ type: ActionType.SET_IS_VERIFYING_DONE });
        showErrorMessage(error);
        return false;
      }
      // FIXME: Automatically disabled during ESLint config update
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (error) {
      dispatch({ type: ActionType.SET_IS_VERIFYING_DONE });
      showErrorMessage("Error occurred while signing in, please try again.");
      return false;
    }

    updateAgentLoggedInTime();
    logEvent(USER_EVENTS.SIGNED_IN);
    return true;
  };

export const logout =
  (callback?: () => void) =>
  async (dispatch: Dispatch<any>): Promise<void> => {
    try {
      logEvent(APP_V2_APP_EVENTS.LEGACY_LOGOUT_FUNCTION_STARTED);
      dispatch({
        type: ActionType.LOGOUT_REQUESTED,
      });

      logEvent(USER_EVENTS.LOGGED_OUT);

      // Sign out from both Firebase and Cognito
      await firebaseAuth.signOut();
      await getCognitoSingleton().signOut();

      logEvent(APP_V2_APP_EVENTS.LEGACY_LOGOUT_FUNCTION_ENDED);
      callback?.();
    } catch (error) {
      logEvent(APP_V2_APP_EVENTS.LEGACY_LOGOUT_FUNCTION_ERROR, { error });
      // Re-throw the error to maintain existing behavior
      throw error;
    }
  };

export const logOutAgentAuth =
  () =>
  async (dispatch: Dispatch): Promise<void> => {
    await onLoggedOut(dispatch);
  };

export const reloadAgentProfile =
  (logError = true) =>
  async (): Promise<void> => {
    try {
      await firebaseAuth.currentUser?.getIdTokenResult(true);
    } catch (error) {
      if (logError) {
        const err = error as Error;
        logEvent(USER_EVENTS.ONBOARDING_ERROR, {
          message: "Unable to re-load agent profile",
          error: err?.message,
        });
      }
    }
  };

/**
 * @deprecated
 * Do not remove this until we have decoupled authentication from our legacy Redux implementation
 */
export const setIsSignup = (dispatch: Dispatch): void => {
  localStorage.setItem("isSignup", "true");
  dispatch({
    type: ActionType.SET_IS_SIGNUP,
  });
};

/**
 * @deprecated Review this usage.
 */
export const setReferralRate =
  () =>
  async (dispatch: Dispatch): Promise<void> => {
    const referralRate = await getReferralRate();
    dispatch({
      type: ActionType.UPDATE_REFERRAL_RATE,
      data: { referralRate },
    });
  };
